'use client';

/**
 * FormContainer (storefront, live) — owns the react-hook-form instance for
 * the embedded form document. Reuses the shared component's render for the
 * styled shell + DropZone (which renders the live field wrappers as its
 * children), wrapped in a real <form> with submission, success and error
 * handling. Includes a hidden honeypot input (`_hp`) — the server silently
 * drops submissions that fill it.
 *
 * After-submit behavior (success message / redirect) is owned by the
 * FormSubmitButton and read from successRef at submit time.
 */

import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer as Shared } from '@launchstore/shared-puck';
import type { FormContainerProps } from '@launchstore/shared-puck';
import { FormContext } from './FormContext';
import type { FormSuccessBehavior } from './FormContext';

const DEFAULT_SUCCESS: FormSuccessBehavior = {
  successMessage: 'Thank you! Your submission has been received.',
  successRedirectUrl: '',
};

function FormContainerLive(props: FormContainerProps) {
  const { formId } = props;
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState<FormSuccessBehavior>(DEFAULT_SUCCESS);
  const [serverError, setServerError] = useState<string | null>(null);
  const successRef = useRef<FormSuccessBehavior>(DEFAULT_SUCCESS);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<Record<string, unknown>>();

  const onSubmit = async (values: Record<string, unknown>) => {
    if (!formId) return;
    setServerError(null);
    try {
      const res = await fetch(`/api/forms/${encodeURIComponent(formId)}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: values, metadata: { pageUrl: window.location.href } }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || 'Submission failed. Please try again.');
      }
      const behavior = successRef.current || DEFAULT_SUCCESS;
      if (behavior.successRedirectUrl) {
        window.location.href = behavior.successRedirectUrl;
        return;
      }
      setSuccess(behavior);
      setSubmitted(true);
    } catch (err: unknown) {
      setServerError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    }
  };

  if (submitted) {
    return (
      <div className="w-full max-w-md mx-auto flex flex-col items-center gap-2 py-8 text-center">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
          <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
          </svg>
        </span>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{success.successMessage}</p>
      </div>
    );
  }

  return (
    <FormContext.Provider value={{ register, errors, isSubmitting, successRef }}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Honeypot — hidden from humans, irresistible to bots. Server drops filled ones. */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[9999px] h-0 w-0 opacity-0"
          {...register('_hp')}
        />
        {serverError && (
          <p role="alert" className="mx-auto mb-4 max-w-md rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-600">
            {serverError}
          </p>
        )}
        {(Shared.render as any)(props)}
      </form>
    </FormContext.Provider>
  );
}

export const FormContainer: any = {
  ...Shared,
  render: (props: FormContainerProps) => <FormContainerLive {...props} />,
};

export default FormContainer;
